import { useFormContext } from "react-hook-form";

import { TEditData } from "./AssignedTable";

import FormInput from "@/components/form-fields/form-input";
import AvatarText from "@/components/reusables/AvatarText";
import { CustomDropdown } from "@/components/reusables/custom-action-button";
import { SelectMulti } from "@/components/reusables/custom-form-fields/FormSelect";
import EmptyData from "@/components/state-pages/EmptyData";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import * as Table from "@/components/ui/table";
import {
  IProjectAssignedTool,
  ISubProduct,
  ProductType,
} from "@/interfaces/tools.interface";
import { cn } from "@/lib/utils";
import { TUpdateAwaitingTools } from "@/schemas/tools.schema";
import { InputKeyLimiter } from "@/utils/helper-functions/form.helper";

export const CollapsibleAssignedTableBody = ({
  selectedProductDetail,
  editData,
  parentIndex,
  isLoading,
  isUpdating,
  fetchedSingleProduct,
  handleUpdateTools,
  actions,
}: {
  selectedProductDetail: IProjectAssignedTool | undefined;
  editData: TEditData | null;
  parentIndex: number;
  isLoading: boolean;
  isUpdating: boolean;
  fetchedSingleProduct: ISubProduct[] | undefined;
  actions: (props: TEditData) => ActionButtonDataTypes[];
  handleUpdateTools: (data: TUpdateAwaitingTools) => void;
}): JSX.Element => {
  const CustomTableHeader = (): JSX.Element => {
    const commonClasses =
      "capitalize  border-r border-r-white text-primary dark:text-white";

    return (
      <Table.TableHeader className={"bg-[#E4E8F2] dark:bg-primaryLight "}>
        <Table.TableRow>
          <Table.TableHead className={cn(commonClasses)}>Name</Table.TableHead>
          <Table.TableHead className={commonClasses}>Quantity</Table.TableHead>
          <Table.TableHead className={commonClasses}>
            Sub Products
          </Table.TableHead>
          <Table.TableHead className={commonClasses}>Notes</Table.TableHead>
          <Table.TableHead className={commonClasses}>Comments</Table.TableHead>
          <Table.TableHead className={commonClasses}></Table.TableHead>
        </Table.TableRow>
      </Table.TableHeader>
    );
  };

  const form = useFormContext<TUpdateAwaitingTools>();

  return (
    <div className='rounded-md border overflow-clip'>
      <Table.Table>
        <CustomTableHeader />
        <Table.TableBody>
          {selectedProductDetail?.productAssignments?.length ? (
            selectedProductDetail?.productAssignments?.map(
              (singleProduct, index) => {
                const commonCellClassName = "border-r border-r-gray-100";
                const defaultSubProduct =
                  singleProduct?.subProductAssignments?.map((subProduct) => ({
                    label: subProduct?.subProduct?.name || "",
                    value: subProduct?.subProduct?.id || "",
                  })) ?? [];

                const isEditableRow = editData?.id === singleProduct?.id;

                const isProductTypeAHasQuantity =
                  singleProduct?.product?.type === ProductType.Has_Quantity;

                if (isEditableRow) {
                  form.setValue(
                    `items.${parentIndex}.productAssignments.${index}.isAltered`,
                    true,
                    {
                      shouldDirty: false,
                    },
                  );
                } else {
                  form.setValue(
                    `items.${parentIndex}.productAssignments.${index}.isAltered`,
                    false,
                    {
                      shouldDirty: false,
                    },
                  );
                }

                return (
                  <Table.TableRow key={singleProduct.id}>
                    <Table.TableCell
                      className={cn(commonCellClassName, "w-[350px]")}
                    >
                      <AvatarText
                        src={singleProduct?.product?.images?.[0]}
                        name={`${singleProduct?.product?.name || ""}`}
                        title={`${singleProduct?.product?.name || ""}`}
                        classNames={{
                          titleClassName: "line-clamp-1 text-wrap",
                        }}
                      />
                    </Table.TableCell>
                    <Table.TableCell
                      className={cn(commonCellClassName, "w-[250px] px-5")}
                    >
                      <FormInput
                        name={`items.${parentIndex}.productAssignments.${index}.quantity`}
                        form={form}
                        input={{
                          type: "number",
                          readOnly: !isEditableRow,
                          onKeyDown: (e) =>
                            InputKeyLimiter({
                              event: e,
                              maxValue: Infinity,
                              allowDecimal: false,
                            }),
                        }}
                      />
                    </Table.TableCell>
                    <Table.TableCell
                      className={cn(commonCellClassName, "w-[400px] px-5")}
                    >
                      {isProductTypeAHasQuantity ? (
                        <span className='text-center'>---</span>
                      ) : (
                        <>
                          {isEditableRow ? (
                            <SelectMulti
                              control={form.control}
                              name={`items.${parentIndex}.productAssignments.${index}.subProductIds`}
                              isLoading={isLoading}
                              maxCount={5}
                              maxSelection={
                                form.watch(
                                  `items.${parentIndex}.productAssignments.${index}.quantity`,
                                ) ?? undefined
                              }
                              defaultValue={
                                form.watch(
                                  `items.${parentIndex}.productAssignments.${index}.subProductIds`,
                                ) ?? []
                              }
                              options={
                                fetchedSingleProduct
                                  ? [
                                      ...fetchedSingleProduct?.map(
                                        (subProduct) => ({
                                          label: subProduct?.name,
                                          value: subProduct?.id,
                                        }),
                                      ),
                                      ...defaultSubProduct,
                                    ]
                                  : []
                              }
                              isRequired
                            />
                          ) : (
                            <div className='flex items-center justify-center mt-2'>
                              <Button
                                variant={"outline"}
                                className='flex w-full p-1 rounded-md border min-h-10 h-auto items-center justify-between bg-inherit hover:bg-inherit active:scale-100'
                              >
                                {defaultSubProduct?.length ? (
                                  <div className='flex justify-between items-center w-full'>
                                    <div className='flex flex-wrap items-center space-y-1'>
                                      {defaultSubProduct
                                        .map((subProduct) => subProduct?.label)
                                        ?.slice(0, 3)
                                        ?.map((subProductName, idx) => {
                                          return (
                                            <Badge
                                              key={subProductName + idx}
                                              className='mr-2 !text-center items-center'
                                              variant={"secondary"}
                                            >
                                              {subProductName}
                                            </Badge>
                                          );
                                        })}
                                      {defaultSubProduct?.length > 3 && (
                                        <Badge
                                          className='mr-2 !text-center items-center'
                                          variant={"secondary"}
                                        >
                                          +{defaultSubProduct?.length - 3}
                                        </Badge>
                                      )}
                                    </div>
                                  </div>
                                ) : (
                                  <span></span>
                                )}
                              </Button>
                            </div>
                          )}
                        </>
                      )}
                    </Table.TableCell>
                    <Table.TableCell
                      className={cn(commonCellClassName, "px-5")}
                    >
                      <FormInput
                        form={form}
                        name={`items.${parentIndex}.productAssignments.${index}.note`}
                        input={{
                          readOnly: !isEditableRow,
                        }}
                      />
                    </Table.TableCell>
                    <Table.TableCell className={cn("px-5")}>
                      <FormInput
                        form={form}
                        name={`items.${parentIndex}.productAssignments.${index}.description`}
                        input={{
                          readOnly: !isEditableRow,
                        }}
                      />
                    </Table.TableCell>
                    <Table.TableCell>
                      <div
                        className={cn(
                          "[tr:hover_&]:opacity-100 flex justify-end pr-3",
                        )}
                      >
                        {singleProduct?.id === editData?.id ? (
                          <Button
                            variant={"light"}
                            onClick={form.handleSubmit(handleUpdateTools)}
                            isLoading={isUpdating}
                            disabled={!form.formState.isDirty}
                          >
                            Save
                          </Button>
                        ) : (
                          <CustomDropdown
                            data={actions({
                              id: singleProduct?.id,
                              productId: singleProduct?.productId,
                              projectDetailId:
                                selectedProductDetail?.productAssignments[index]
                                  .id,
                              parentIndex,
                            })}
                          />
                        )}
                      </div>
                    </Table.TableCell>
                  </Table.TableRow>
                );
              },
            )
          ) : (
            <Table.TableRow>
              <Table.TableCell colSpan={7}>
                <EmptyData title='Assigned Tools' />
              </Table.TableCell>
            </Table.TableRow>
          )}
        </Table.TableBody>
      </Table.Table>
    </div>
  );
};
